import { useState, useEffect, useCallback, useRef } from "react";
import { Upload, Trash2, Link2, Star, Edit3, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, API } from "./AdminShared";

const CATEGORY_OPTIONS = ["Coaching", "Training", "Workshops", "Events", "Other"];

export default function AdminGalleryTab({ token }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ caption: "", category: "" });
  const [filter, setFilter] = useState("all"); // all | featured
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api(token).get("/gallery").then((r) => setImages(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setUploading(true);
    let count = 0;
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      try {
        const formData = new FormData();
        formData.append("file", file);
        await api(token).post("/gallery", formData, { headers: { "Content-Type": "multipart/form-data" } });
        count++;
      } catch { toast.error(`Failed: ${file.name}`); }
    }
    if (count) toast.success(`${count} image${count > 1 ? "s" : ""} uploaded`);
    setUploading(false);
    load();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
    if (files.length) upload(files);
  };

  const copyUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl).then(() => toast.success("URL copied!")).catch(() => {});
  };

  const toggleFeatured = async (img) => {
    await api(token).put(`/gallery/${img.id}`, { featured: !img.featured });
    toast.success(img.featured ? "Removed from public gallery" : "Added to public gallery");
    load();
  };

  const startEdit = (img) => {
    setEditingId(img.id);
    setEditDraft({ caption: img.caption || "", category: img.category || "" });
  };

  const saveEdit = async (id) => {
    await api(token).put(`/gallery/${id}`, editDraft);
    toast.success("Saved");
    setEditingId(null);
    load();
  };

  const filtered = filter === "featured" ? images.filter((i) => i.featured) : images;
  const featuredCount = images.filter((i) => i.featured).length;

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 cursor-pointer ${
          dragOver ? "border-gg-red bg-gg-red/5 scale-[1.01]" : "border-white/10 hover:border-white/20"
        }`}
        onClick={() => fileRef.current?.click()}
      >
        <Upload size={24} className={`mx-auto mb-2 transition-colors ${dragOver ? "text-gg-red" : "text-zinc-600"}`} />
        <p className="text-zinc-400 text-sm font-heading uppercase tracking-wider">
          {uploading ? "Uploading..." : dragOver ? "Drop images here" : "Drag & drop images here"}
        </p>
        <p className="text-zinc-600 text-xs mt-1">or click to browse — supports JPG, PNG, GIF, WebP (max 10MB each)</p>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { if (e.target.files.length) upload(Array.from(e.target.files)); }} />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            data-testid="gallery-filter-all"
            className={`text-xs font-heading uppercase tracking-wider px-3 py-1.5 rounded-lg transition-colors ${filter === "all" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}
          >All ({images.length})</button>
          <button
            onClick={() => setFilter("featured")}
            data-testid="gallery-filter-featured"
            className={`text-xs font-heading uppercase tracking-wider px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${filter === "featured" ? "bg-gg-red/20 text-gg-red" : "text-zinc-500 hover:text-gg-red"}`}
          ><Star size={11} /> Public ({featuredCount})</button>
        </div>
        <p className="text-zinc-500 text-[10px]">Star an image to publish it on the public Gallery page.</p>
      </div>

      {filtered.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map((img) => (
            <div key={img.id} className={`group relative aspect-square rounded-lg overflow-hidden border bg-gg-black transition-colors ${img.featured ? "border-gg-red/60" : "border-white/10 hover:border-white/30"}`} data-testid={`gallery-item-${img.id}`}>
              <img src={`${API.replace('/api', '')}${img.url}`} alt={img.caption || img.filename} className="w-full h-full object-cover" />

              {img.featured && (
                <div className="absolute top-1.5 left-1.5 bg-gg-red text-white text-[9px] font-heading uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Star size={9} fill="currentColor" /> Public
                </div>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-colors flex flex-col items-stretch justify-end gap-1 opacity-0 group-hover:opacity-100 p-2">
                {editingId === img.id ? (
                  <div className="space-y-1.5">
                    <Input value={editDraft.caption} onChange={(e) => setEditDraft({ ...editDraft, caption: e.target.value })} placeholder="Caption" className="bg-gg-surface border-white/10 text-white rounded text-[11px] h-7" />
                    <select value={editDraft.category} onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })} className="w-full bg-gg-surface border border-white/10 text-white rounded text-[11px] h-7 px-2">
                      <option value="">— Category —</option>
                      {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Button size="sm" onClick={() => saveEdit(img.id)} className="bg-gg-red hover:bg-gg-red-hover text-white rounded h-7 text-[10px] uppercase tracking-wider w-full"><Check size={10} className="mr-1" /> Save</Button>
                  </div>
                ) : (
                  <>
                    {(img.caption || img.category) && (
                      <div className="text-white text-[10px] mb-1 space-y-0.5 text-center">
                        {img.caption && <p className="font-heading uppercase tracking-wider truncate">{img.caption}</p>}
                        {img.category && <p className="text-zinc-400 text-[9px]">{img.category}</p>}
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => toggleFeatured(img)} title={img.featured ? "Unpublish" : "Publish"} className={`h-7 w-7 p-0 ${img.featured ? "text-gg-red hover:text-white" : "text-white hover:text-gg-red"}`}><Star size={13} fill={img.featured ? "currentColor" : "none"} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(img)} title="Edit caption" className="h-7 w-7 p-0 text-white hover:text-gg-red"><Edit3 size={13} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => copyUrl(img.url)} title="Copy URL" className="h-7 w-7 p-0 text-white hover:text-gg-red"><Link2 size={13} /></Button>
                      <Button size="sm" variant="ghost" onClick={async () => { if (confirm("Delete this image?")) { await api(token).delete(`/gallery/${img.id}`); load(); toast.success("Deleted"); } }} title="Delete" className="h-7 w-7 p-0 text-white hover:text-red-500"><Trash2 size={13} /></Button>
                    </div>
                  </>
                )}
              </div>

              {!img.caption && !img.category && (
                <p className="absolute bottom-0 left-0 right-0 bg-black/70 text-zinc-500 text-[9px] px-2 py-1 truncate group-hover:opacity-0 transition-opacity">{img.filename}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <p className="text-zinc-500 text-center text-sm py-10">{filter === "featured" ? "No images marked as public yet. Star an image to feature it." : "No images yet — drop some files above to get started."}</p>
      )}
    </div>
  );
}
